// Enum → fan wording. No template ever formats a raw API enum: it asks this
// map (through useLabels()). `test/site/lint/labels.enums.test.ts` asserts
// every enum value the public API can return has an entry here.
//
// Values follow audit TERMINOLOGY.md §2b and §2e (the admin-v2 vocabulary:
// Live = in progress, Final = done, Upcoming = not started). The words are in
// the site-core catalogue's `labels` area (#1118) and follow the active
// language; the *_TEXT maps below are their English, for tests and docs.

import { messages } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import labelsEn from '@/i18n/catalogs/site-core/labels/en'

/** The labels layer's words in the active language. */
export function labelWords(): typeof labelsEn {
  return messages(siteCoreMessages).labels
}

// ────────────────────────────────────────────────────────────────────────
// Enum value lists (the public API contract, TERMINOLOGY.md §3)
// ────────────────────────────────────────────────────────────────────────

export const GAME_STATUSES = ['scheduled', 'in_progress', 'completed', 'postponed', 'cancelled'] as const
export type GameStatus = (typeof GAME_STATUSES)[number]

export const COMPETITION_STATUSES = ['draft', 'active', 'completed'] as const
export type CompetitionStatus = (typeof COMPETITION_STATUSES)[number]

export const COMPETITION_FORMATS = ['round_robin', 'single_elimination'] as const
export type CompetitionFormat = (typeof COMPETITION_FORMATS)[number]

export const BRACKET_TEAM_STATUSES = ['alive', 'eliminated', 'champion'] as const
export type BracketTeamStatus = (typeof BRACKET_TEAM_STATUSES)[number]

/** The stats scope on player and team pages (`StatsView` in the pages). */
export const STATS_VIEWS = ['lifetime', 'season', 'tournament'] as const
export type StatsView = (typeof STATS_VIEWS)[number]

/** A registration entry's status (`registration_entries_status_chk`,
 *  migration 205), shown in My registrations (registration plan step 9). */
export const ENTRY_STATUSES = ['invited', 'pending', 'waiting_approval', 'registered', 'rejected', 'waitlisted', 'cancelled'] as const
export type EntryStatus = (typeof ENTRY_STATUSES)[number]

// ────────────────────────────────────────────────────────────────────────
// Game status
// ────────────────────────────────────────────────────────────────────────

/** What the fan is told about a game's state. `liveNoUpdates` is a live
 *  game whose league has live updates off (a deliberately weaker promise).
 *  `scoreSoon` (start time passed, still scheduled, no score) and `dateTba`
 *  (no start time) are only produced when the caller passes `startsAt`,
 *  which classic never does. */
export type GameStatusKind =
  | 'upcoming'
  | 'live'
  | 'liveNoUpdates'
  | 'final'
  | 'postponed'
  | 'cancelled'
  | 'scoreSoon'
  | 'dateTba'

/**
 * Where the status is shown. Today the public site uses different words per
 * surface for the same state; the variants keep that until the terminology
 * PR collapses them (TERMINOLOGY.md §2b).
 *   badge   — the game page header pill (GameDetail)
 *   compact — list rows and cards (ScheduleRoundRobin, ScheduleBracket, Home)
 *   venue   — the venue page game list (VenueDetail)
 */
export type GameStatusVariant = 'badge' | 'compact' | 'venue'

/** The final state's words are sport-aware (Final / Full time), so they come
 *  from the sport pack's terms: `final` on the badge, `finalShort` elsewhere. */
export const FINAL_TERM: Record<GameStatusVariant, 'final' | 'finalShort'> = {
  badge: 'final',
  compact: 'finalShort',
  venue: 'finalShort',
}

/** Every variant says the same today (the terminology PR may split them). English. */
export const GAME_STATUS_TEXT: Record<GameStatusVariant, Record<Exclude<GameStatusKind, 'final'>, string>> = {
  badge: labelsEn.gameStatus,
  compact: labelsEn.gameStatus,
  venue: labelsEn.gameStatus,
}

/** Map an API game status (plus the league's live-updates switch) to the
 *  kind the fan is told. Unknown statuses read as upcoming, like today's
 *  `default:` branches. Pass `timing` to tell undated and overdue
 *  scheduled games apart (new templates); omit it for today's behaviour. */
export function gameStatusKind(
  status: string | null | undefined,
  liveUpdates = true,
  timing?: { startsAt: string | null; hasScore?: boolean; now?: Date | number }
): GameStatusKind {
  if (timing && (status === 'scheduled' || status == null)) {
    if (timing.startsAt === null) return 'dateTba'
    const start = Date.parse(timing.startsAt)
    const now = timing.now === undefined ? Date.now() : +timing.now
    if (!Number.isNaN(start) && start <= now && !timing.hasScore) return 'scoreSoon'
  }
  switch (status) {
    case 'in_progress':
      return liveUpdates ? 'live' : 'liveNoUpdates'
    case 'completed':
      return 'final'
    case 'postponed':
      return 'postponed'
    case 'cancelled':
      return 'cancelled'
    default:
      return 'upcoming'
  }
}

// ────────────────────────────────────────────────────────────────────────
// Competition progress (Schedule header pill)
// ────────────────────────────────────────────────────────────────────────

/** Mirrors backend fan-ai `tournamentPhase` (#207): `status` is a publishing
 *  flag, so "has play started" comes from the games. */
export type CompetitionPhase = 'not_started' | 'in_progress' | 'completed'

export function competitionPhase(c: { status: string; gamesStarted: number }): CompetitionPhase {
  if (c.status === 'completed') return 'completed'
  if (c.gamesStarted > 0) return 'in_progress'
  return 'not_started'
}

export const COMPETITION_PROGRESS = {
  // English (labelWords().progress is the active language's).
  notStarted: (startsAt: string | null): string => (startsAt ? labelsEn.progress.startsOn(startsAt) : labelsEn.progress.upcoming),
  inProgress: labelsEn.progress.inProgress,
  completed: labelsEn.progress.completed,
}

// ────────────────────────────────────────────────────────────────────────
// Format, bracket team status, stats view
// ────────────────────────────────────────────────────────────────────────

/** The catalogue's key for each format. */
export const FORMAT_KEY: Record<CompetitionFormat, keyof typeof labelsEn.format> = {
  round_robin: 'roundRobin',
  single_elimination: 'bracket',
}

/** English. */
export const FORMAT_TEXT: Record<CompetitionFormat, string> = {
  round_robin: labelsEn.format[FORMAT_KEY.round_robin],
  single_elimination: labelsEn.format[FORMAT_KEY.single_elimination],
}

/** Without the trophy: classic prefixes its own "🏆 " (presentation). English. */
export const BRACKET_STATUS_TEXT: Record<BracketTeamStatus, string> = labelsEn.bracketStatus

/** The catalogue's key for each stats view (`tournament` is a Competition). */
export const STATS_VIEW_KEY: Record<StatsView, keyof typeof labelsEn.statsView.player> = {
  lifetime: 'lifetime',
  season: 'season',
  tournament: 'competition',
}

/** Stats-scope tab labels. Players and teams word the career scope
 *  differently on purpose (TERMINOLOGY.md §2e). English. */
export const STATS_VIEW_TEXT: Record<'player' | 'team', Record<StatsView, string>> = {
  player: {
    lifetime: labelsEn.statsView.player.lifetime,
    season: labelsEn.statsView.player.season,
    tournament: labelsEn.statsView.player.competition,
  },
  team: {
    lifetime: labelsEn.statsView.team.lifetime,
    season: labelsEn.statsView.team.season,
    tournament: labelsEn.statsView.team.competition,
  },
}

// ────────────────────────────────────────────────────────────────────────
// Bracket round names
// ────────────────────────────────────────────────────────────────────────

/** English. round_number 0 is `playIn`: display only, never match stored
 *  slot labels against it (they say "Winner of Play-In G{n}", see
 *  STORED_PLAY_IN_SLOT_PREFIX). */
export const ROUND_TEXT = labelsEn.round

/**
 * The prefix the backend writes into bracket slot labels for play-in
 * winners (`backend/src/services/tournament-scheduler.ts`). This is STORED
 * data, not a display label: it never changes with the wording.
 */
export const STORED_PLAY_IN_SLOT_PREFIX = 'Winner of Play-In'

/**
 * Name of a bracket round. Round 0 is the play-in. Otherwise the name comes
 * from how many rounds remain: by default `roundsTotal - roundNumber + 1`
 * (rounds numbered 1..n); pass `remaining` when the round numbers have gaps.
 */
export function roundName(roundNumber: number, roundsTotal: number, remaining?: number): string {
  const round = labelWords().round
  if (roundNumber === 0) return round.playIn
  const left = remaining ?? roundsTotal - roundNumber + 1
  if (left === 1) return round.final
  if (left === 2) return round.semifinals
  if (left === 3) return round.quarterfinals
  return round.numbered(roundNumber)
}

// ────────────────────────────────────────────────────────────────────────
// Registration entry status (My registrations, registration plan step 9)
// ────────────────────────────────────────────────────────────────────────

export type EntryStatusTone = 'positive' | 'pending' | 'negative' | 'neutral'

export const ENTRY_STATUS_TEXT: Record<EntryStatus, { label: string; tone: EntryStatusTone }> = {
  invited: { label: 'Invited', tone: 'pending' },
  pending: { label: 'Pending', tone: 'pending' },
  waiting_approval: { label: 'Awaiting approval', tone: 'pending' },
  registered: { label: 'Registered', tone: 'positive' },
  rejected: { label: 'Not accepted', tone: 'negative' },
  waitlisted: { label: 'Waitlisted', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
}

/** The fan label for an entry status; null for an unknown one (render no pill, never the raw value). */
export function entryStatusText(status: string): { label: string; tone: EntryStatusTone } | null {
  return Object.prototype.hasOwnProperty.call(ENTRY_STATUS_TEXT, status) ? ENTRY_STATUS_TEXT[status as EntryStatus] : null
}

/** Step 12: the payment wording on My registrations. 'none' has no text
 *  (a free entry shows no payment pill). */
export const PAYMENT_STATUS_TEXT: Record<'pending' | 'paid' | 'refunded', { label: string; tone: EntryStatusTone }> = {
  pending: { label: 'Payment due', tone: 'pending' },
  paid: { label: 'Paid', tone: 'positive' },
  refunded: { label: 'Refunded', tone: 'neutral' },
}

/** Step 35: an entry the waiver holds (unsigned while pending or waiting
 *  for approval, the server's `due`): it reads this in place of its status,
 *  even when its payment is in. */
export const WAIVER_DUE_TEXT: { label: string; tone: EntryStatusTone } = { label: 'Waiting for the waiver', tone: 'pending' }

/** The label for a payment status; null for 'none' or an unknown value (never the raw value). */
export function paymentStatusText(s: string): { label: string; tone: EntryStatusTone } | null {
  return Object.prototype.hasOwnProperty.call(PAYMENT_STATUS_TEXT, s)
    ? PAYMENT_STATUS_TEXT[s as keyof typeof PAYMENT_STATUS_TEXT]
    : null
}
